import type {
  ApiKeyDto,
  AuditEntryDto,
  Health,
  ImportResult,
  LoginResponse,
  MessageDetail,
  MessageDto,
  OverviewStats,
  Paginated,
  RecipientDto,
  TemplateDto,
  TemplateSyncResult,
  UserDto,
} from '@wa/shared';

import { qs, request } from './client.js';

export interface RecipientDetail extends RecipientDto {
  consentHistory: {
    id: string;
    action: 'opted_in' | 'opted_out';
    source: string;
    actor: string | null;
    note: string | null;
    createdAt: string;
  }[];
  recentMessages: MessageDto[];
}

export interface CatalogueEntry {
  name: string;
  language: string;
  category: string;
  headerType: string;
  body: string;
  footer: string | null;
  examples: string[];
  params: string[];
  notes: string | null;
  paramCount: number;
  /** Meta's status for this template, or null if never submitted. */
  status: string | null;
  rejectedReason: string | null;
  submitted: boolean;
}

export interface SubmitResult {
  name: string;
  ok: boolean;
  id?: string;
  status?: string;
  error?: string;
}

export interface SearchResult {
  type: 'recipient' | 'message' | 'template';
  id: string;
  title: string;
  subtitle: string;
  href: string;
}

export const api = {
  search: (q: string) =>
    request<{ query: string; results: SearchResult[] }>(`/v1/search${qs({ q })}`),

  auth: {
    login: (email: string, password: string) =>
      request<LoginResponse>('/v1/auth/login', { method: 'POST', body: { email, password } }),
    logout: () => request<void>('/v1/auth/logout', { method: 'POST' }),
    me: () => request<{ id: string; email: string; role: 'admin' | 'ops' }>('/v1/auth/me'),
    users: () => request<{ data: UserDto[] }>('/v1/auth/users'),
    createUser: (body: { email: string; password: string; role: 'admin' | 'ops' }) =>
      request<UserDto>('/v1/auth/users', { method: 'POST', body }),
    updateUser: (id: string, body: { role?: string; active?: boolean; password?: string }) =>
      request<UserDto>(`/v1/auth/users/${id}`, { method: 'PATCH', body }),
    apiKeys: () => request<{ data: ApiKeyDto[] }>('/v1/auth/api-keys'),
    createApiKey: (body: { name: string; scopes: string[] }) =>
      request<ApiKeyDto & { key: string }>('/v1/auth/api-keys', { method: 'POST', body }),
    revokeApiKey: (id: string) => request<void>(`/v1/auth/api-keys/${id}`, { method: 'DELETE' }),
    audit: (params: Record<string, unknown>) =>
      request<Paginated<AuditEntryDto>>(`/v1/auth/audit${qs(params)}`),
  },

  health: {
    get: () => request<Health>('/v1/health'),
    overview: () => request<OverviewStats>('/v1/health/overview'),
  },

  messages: {
    create: (body: Record<string, unknown>) =>
      request<{ id: string; status: string; duplicate: boolean }>('/v1/messages', {
        method: 'POST',
        body,
      }),
    list: (params: Record<string, unknown>) =>
      request<Paginated<MessageDto>>(`/v1/messages${qs(params)}`),
    get: (id: string) => request<MessageDetail>(`/v1/messages/${id}`),
    retry: (id: string, force = false) =>
      request<{ id: string; status: string }>(`/v1/messages/${id}/retry`, {
        method: 'POST',
        body: { force },
      }),
    cancel: (id: string) =>
      request<{ id: string; status: string }>(`/v1/messages/${id}/cancel`, { method: 'POST' }),
  },

  recipients: {
    list: (params: Record<string, unknown>) =>
      request<Paginated<RecipientDto>>(`/v1/recipients${qs(params)}`),
    get: (phone: string) => request<RecipientDetail>(`/v1/recipients/${encodeURIComponent(phone)}`),
    create: (body: Record<string, unknown>) =>
      request<RecipientDto>('/v1/recipients', { method: 'POST', body }),
    update: (phone: string, body: Record<string, unknown>) =>
      request<RecipientDto>(`/v1/recipients/${encodeURIComponent(phone)}`, {
        method: 'PATCH',
        body,
      }),
    remove: (phone: string) =>
      request<void>(`/v1/recipients/${encodeURIComponent(phone)}`, { method: 'DELETE' }),
    optIn: (phone: string, body: { source: string; note?: string }) =>
      request<RecipientDto>(`/v1/recipients/${encodeURIComponent(phone)}/opt-in`, {
        method: 'POST',
        body,
      }),
    optOut: (phone: string, body: { source: string; note: string }) =>
      request<RecipientDto & { cancelledMessages: number }>(
        `/v1/recipients/${encodeURIComponent(phone)}/opt-out`,
        { method: 'POST', body },
      ),
    import: (body: { csv: string; consentSource: string }) =>
      request<ImportResult>('/v1/recipients/import', { method: 'POST', body }),
  },

  templates: {
    list: () => request<{ data: TemplateDto[] }>('/v1/templates'),
    sync: () => request<TemplateSyncResult>('/v1/templates/sync', { method: 'POST' }),
    catalogue: () => request<{ data: CatalogueEntry[] }>('/v1/templates/catalogue'),
    submit: (names: string[]) =>
      request<{ results: SubmitResult[]; sync: TemplateSyncResult }>('/v1/templates/submit', {
        method: 'POST',
        body: { names },
      }),
  },
};
